import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { formatDay, todayUk } from '@nu/shared';
import { api } from '../../api/client.js';
import { useAutoRefresh } from '../../useAutoRefresh.js';
import BookingsWeek from './BookingsWeek.jsx';
import BookingsMonth, { monthGrid } from './BookingsMonth.jsx';
import { BookingsHead, FILL_LEGEND, addDays, mondayOf, useBookingEditor, useBookingRefs, useLeave } from './shared.jsx';
import { problem } from './util.js';

// Bookings calendar, for planning: Week (a row per team, a column per day;
// drag to move, + to book) or Month (each day's load per team, or one
// team's bookings). Week is the default. Office only, online only.

const monthOf = (day) => `${day.slice(0, 7)}-01`;
function shiftMonth(monthStart, n) {
  const [y, m] = monthStart.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 10);
}
const monthLabel = (monthStart) => new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${monthStart}T12:00:00Z`));

export default function BookingsCalendar({ me }) {
  const canEdit = me.capabilities.includes('page.bookings-calendar.edit');
  const today = todayUk();
  const { types, teams, people, colours } = useBookingRefs();
  const [view, setView] = useState('week');
  const [weekStart, setWeekStart] = useState(() => mondayOf(today));
  const [monthStart, setMonthStart] = useState(() => monthOf(today));
  const [teamId, setTeamId] = useState('');
  const [rows, setRows] = useState(null);
  const [waiting, setWaiting] = useState([]); // revisits with no date yet
  const [error, setError] = useState(null);

  const [from, to] = useMemo(() => {
    if (view === 'week') return [weekStart, addDays(weekStart, 6)];
    const g = monthGrid(monthStart);
    return [g.first, g.last];
  }, [view, weekStart, monthStart]);
  const leave = useLeave(from, to);

  const load = useCallback(async () => {
    setError(null);
    const p = new URLSearchParams({ from, to });
    if (teamId) p.set('teamId', teamId);
    try {
      // Undated bookings come back with a from-date; the calendar has no day for them.
      const [r, w] = await Promise.all([api(`/bookings?${p}`), api('/bookings?revisits=waiting').catch(() => ({ bookings: [] }))]);
      setRows(r.bookings.filter((b) => b.bookingDate));
      setWaiting(w.bookings);
    } catch (e) {
      setError(problem(e));
    }
  }, [from, to, teamId]);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load);

  const { notice, setNotice, openBooking, openNew, modal } = useBookingEditor({ canEdit, types, teams, reload: load });

  // Drag on the week: new team and/or day, same slot.
  const move = async (b, newTeamId, date) => {
    setNotice(null);
    try {
      await api(`/bookings/${b.id}`, { method: 'PUT', body: { teamId: newTeamId, bookingDate: date } });
      const team = teams.find((t) => t.id === newTeamId);
      setNotice({ tone: 'ok', text: `${b.reference} moved to ${team ? team.name : 'no team'} on ${formatDay(date)}.` });
    } catch (e) {
      setNotice({ tone: 'bad', text: problem(e) });
    }
    load();
  };

  const step = (n) => (view === 'week' ? setWeekStart((w) => addDays(w, 7 * n)) : setMonthStart((m) => shiftMonth(m, n)));
  const goToday = () => (view === 'week' ? setWeekStart(mondayOf(today)) : setMonthStart(monthOf(today)));
  const switchTo = (v) => {
    // Keep roughly the same dates when switching.
    if (v === 'month' && view === 'week') setMonthStart(monthOf(weekStart <= today && today <= addDays(weekStart, 6) ? today : weekStart));
    if (v === 'week' && view === 'month') setWeekStart(mondayOf(monthStart <= today && today < shiftMonth(monthStart, 1) ? today : monthStart));
    setView(v);
  };
  const shownTeams = teams.filter((t) => !teamId || (teamId !== 'none' && String(t.id) === teamId));

  return (
    <div className="stack">
      <BookingsHead
        title="Bookings calendar"
        intro="Plan the installs: who's doing what, and when. Click a booking to see or change it. Find bookings by reference or postcode in the Bookings list."
        canEdit={canEdit}
        onNew={openNew}
        onImported={load}
        notice={notice}
      />

      <section className="card">
        <div className="filters">
          <div className="view-toggle" role="group" aria-label="View">
            <button type="button" className={`btn${view === 'week' ? ' active' : ''}`} aria-pressed={view === 'week'} onClick={() => switchTo('week')}>Week</button>
            <button type="button" className={`btn${view === 'month' ? ' active' : ''}`} aria-pressed={view === 'month'} onClick={() => switchTo('month')}>Month</button>
          </div>
          <div className="week-nav" role="group" aria-label={view === 'week' ? 'Week' : 'Month'}>
            <button type="button" className="btn icon-only" aria-label={`Previous ${view}`} onClick={() => step(-1)}><ChevronLeft size={18} aria-hidden="true" /></button>
            <button type="button" className="btn" onClick={goToday}>{view === 'week' ? 'This week' : 'This month'}</button>
            <button type="button" className="btn icon-only" aria-label={`Next ${view}`} onClick={() => step(1)}><ChevronRight size={18} aria-hidden="true" /></button>
            <span className="week-label">{view === 'week' ? `${formatDay(weekStart)} to ${formatDay(addDays(weekStart, 6))}` : monthLabel(monthStart)}</span>
          </div>
          <select value={teamId} onChange={(e) => setTeamId(e.target.value)} aria-label="Team">
            <option value="">All teams</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}{t.active ? '' : ' (closed)'}</option>)}
            <option value="none">Not on a team</option>
          </select>
        </div>

        {error && <div className="empty"><p>{error}</p><button type="button" className="btn" onClick={load}>Try again</button></div>}
        {!error && !rows && <p className="muted">Loading…</p>}
        {rows && view === 'week' && waiting.length > 0 && (
          <div className="revisit-tray">
            <p className="small"><RotateCcw size={14} aria-hidden="true" /> <strong>Revisits waiting for a date ({waiting.length})</strong>{canEdit ? ': once Network Plus confirms the day with the customer, drag one onto the team and day. A revisit uses half a slot.' : '.'}</p>
            <div className="revisit-chips">
              {waiting.map((b) => (
                <button key={b.id} type="button" className="cal-chip cal-revisit" draggable={canEdit} title={`${b.reference}: revisit (defect) of ${b.revisitOf.reference}\n${[b.addressLine1, b.town, b.postcode].filter(Boolean).join(', ')}`}
                  onDragStart={(e) => { e.dataTransfer.setData('text/plain', String(b.id)); e.dataTransfer.effectAllowed = 'move'; }}
                  onClick={() => openBooking(b.id)}>
                  {b.reference} <span className="small">{b.postcode}</span><span className="chip-rv" aria-label="Revisit">RV</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {rows && view === 'week' && (
          <>
            <BookingsWeek
              waiting={waiting}
              weekStart={weekStart}
              today={today}
              bookings={rows}
              teams={shownTeams}
              people={people}
              leave={leave}
              colours={colours}
              canEdit={canEdit}
              onOpen={openBooking}
              onNew={openNew}
              onMove={move}
            />
            <p className="small muted">
              {canEdit ? 'Click + to book into a team and day, or drag a booking to move it. ' : ''}
              The numbers are bookings against the team&apos;s daily target; a revisit (RV) is half a slot, and cancelled and no-access visits don&apos;t count.
              Bookings without a date are in the Bookings list.
            </p>
            <FillLegend />
          </>
        )}
        {rows && view === 'month' && (
          <>
            <BookingsMonth
              monthStart={monthStart}
              today={today}
              bookings={rows}
              teams={shownTeams}
              teamFilter={teamId}
              people={people}
              leave={leave}
              colours={colours}
              canEdit={canEdit}
              onOpen={openBooking}
              onNew={openNew}
              onPickDay={(date) => { setWeekStart(mondayOf(date)); setView('week'); }}
            />
            <p className="small muted">
              {teamId && teamId !== 'none'
                ? `${canEdit ? 'Click + to book into a day. ' : ''}Pick All teams to see every team's load.`
                : 'Each day shows bookings against each team’s daily target. Pick a team to see its bookings.'}
              {' '}Click a date to open that week.
            </p>
            <FillLegend />
          </>
        )}
      </section>
      {modal}
    </div>
  );
}

function FillLegend() {
  return (
    <p className="fill-legend small">
      <span className="muted">Booked against the team&apos;s target:</span>
      {FILL_LEGEND.map((l) => <span key={l.level} className={`fill-key fill-${l.level}`}>{l.label}</span>)}
    </p>
  );
}
